import styled from "@emotion/styled";
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import theme from "../theme";
import { memo, useEffect, useRef, useState } from "react";
import { useWindowSize } from "@reactuses/core";
import { SrLable } from "./common/SrLabel";

export interface menuItemType {
  value: any;
  title: string;
}

type Prop = {
  open?: boolean;
  handleClose?: () => void;
  handleOpen?: () => void;
  filter: any;
  handleChange?: (event: SelectChangeEvent<any>) => void;
  title?: string;
  menuItems: menuItemType[];
  width?: any;
  color?: string;
  isWhite?: boolean;
  disabled?: boolean;
  mr?: string;
  filterWord?: string; // 필터 항목 뒤에 붙일 단어
  useSearch?: boolean; // 메뉴 아이템 항목 검색 가능 여부
  fontSize?: string;
  srLabel: string; // 스크린리더용 라벨
};

const { md } = theme.screen;

const SelectBox = memo(
  ({
    open,
    handleClose,
    handleOpen,
    filter,
    handleChange,
    title,
    menuItems,
    width = 124,
    color = theme.color.neutral_500,
    isWhite = false,
    disabled,
    mr,
    filterWord,
    useSearch,
    srLabel,
  }: Prop) => {
    const [keyword, setKeyword] = useState("");
    const [filteredItems, setFilteredItems] = useState<menuItemType[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const { width: windowWidth } = useWindowSize();

    const handleChangeText = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setKeyword(event.target.value);
      inputRef.current?.focus();
    };

    useEffect(() => {
      if (!useSearch) return;
      const filteredOption: menuItemType[] = menuItems.filter((el) =>
        el.title.toLowerCase().includes(keyword.toLowerCase())
      );
      setFilteredItems(filteredOption);

      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }, [keyword, menuItems]);

    useEffect(() => {
      if (useSearch) {
        inputRef.current?.focus();
      }
      if (!useSearch) {
        setKeyword("");
      }
    }, [keyword, inputRef]);

    // console.log(document.activeElement);
    // console.log(keyword);
    // console.log(filteredItems);

    return (
      <FormControl
        size="small"
        sx={{ minWidth: windowWidth > md ? "124px" : `none` }}
      >
        {title && <SelectHeader>{title}</SelectHeader>}
        <SrLable labelText={srLabel} id={srLabel} />
        <CustomSelect
          labelId={srLabel} // 라벨과 연결
          disabled={disabled === undefined ? false : disabled}
          open={open}
          onClose={() => {
            if (handleClose) {
              handleClose();
            }
            setKeyword("");
          }}
          onOpen={() => {
            if (handleOpen) {
              handleOpen();
            }
          }}
          value={filter}
          onChange={handleChange}
          sx={{
            width: width,
            height: windowWidth <= md ? "24px" : "40px",
            textAlign: "center",
            fontSize:
              windowWidth <= md
                ? theme.fontSize.FONT_2
                : theme.fontSize.FONT_1,
            ".MuiOutlinedInput-notchedOutline": {
              backgroundColor: isWhite
                ? theme.color.white
                : theme.color.neutral_100,

            borderRadius: "3px",
            borderColor: "rgba(228, 219, 233, 0.25)",
          },
          ".MuiSelect-select": {
            color: color,
            zIndex: 1,
            padding: `3.5px 14px`,
            lineHeight: windowWidth <= md ? "24px" : "40px",
          },
          " .MuiSvgIcon-root": {
            zIndex: 1,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.color.neutral_100,
          },
          mr: mr ? mr : "15px",
          "&:hover": {
            "&& fieldset": {
              borderColor: theme.color.neutral_400,
            },
          },
        }}
        inputProps={{
          MenuProps: {
            MenuListProps: {
              sx: {
                maxHeight: 382,
                overflowY: "auto",
                backgroundColor: isWhite
                  ? theme.color.white
                  : theme.color.neutral_100,
                color: theme.color.neutral_500,
                ".MuiMenuItem-root": {
                  height: windowWidth > md ? "48px" : "30px",
                  minHeight: windowWidth > md ? "48px" : "30px",
                  fontSize:
                    windowWidth > md
                      ? theme.fontSize.BOTTOM_SHEET_1
                      : theme.fontSize.FONT_2,
                },
              },
            },
          },
        }}
      >
        {useSearch && (
          <TextField
            onChange={handleChangeText}
            value={keyword}
            label="항목 검색"
            size="small"
            variant="filled"
            inputRef={inputRef}
            sx={{
              width: "100%",
              color: theme.color.green_500,
              "& .MuiFormLabel-root.Mui-focused": {
                color: theme.color.green_500,
              },
              "& .MuiInputBase-root::after": {
                borderBottomColor: theme.color.green_500,
                color: theme.color.green_500,
              },
            }}
            focused
          />
        )}
        {useSearch &&
          filteredItems.map((item) => (
            <MenuItem
              value={item.value == null ? 18 : item.value}
              key={item.value == null ? 18 : item.value}
              sx={{
                justifyContent: "center",
                width: "100%",
              }}
            >
              {item.title} {filterWord ? filterWord : ""}
            </MenuItem>
          ))}

          {!useSearch &&
            menuItems.map((item) => (
              <MenuItem
                value={item.value == null ? 18 : item.value}
                key={item.value == null ? 18 : item.value}
                sx={{ justifyContent: "center", width: "100%" }}
              >
                {item.title} {filterWord ? filterWord : ""}
              </MenuItem>
            ))}
        </CustomSelect>
      </FormControl>
    );
  }
);

const SelectHeader = styled.span`
  font-weight: 400;
  font-size: 11px;
  line-height: 13px;
  color: ${theme.color.neutral_500};
  margin-bottom: 5px;
  padding-left: 2px;

  @media (max-width: ${md}px) {
    font-size: ${theme.fontSize.FONT_3};
  }
`;

const CustomSelect = styled(Select)`
  .MuiList-root .MuiMenu-list {
    height: 200px;
  }
`;

export default SelectBox;
